import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { Card } from "react-bootstrap";
import cn from "classnames";
import styles from "./DownloadsOverChart.module.css";
import Calender, { CalenderBtnTheme } from "../../common/Calender/Calender";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchLineGraphData } from "../../../redux/slices/dashboard/actions";
import { startOfYear, endOfYear } from "date-fns";

interface CustomTickProps {
  x: number;
  y: number;
  payload: {
    value: string;
  };
}

const tabTitles= ['Subscriptions']

const DownloadsOverChart: React.FC<{ refresh: number }> = ({ refresh }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { lineGraphData, lineGraphLoading } = useSelector((state: RootState) => state.dashboard);
  const [activeTab, setActiveTab] = useState<"Downloads Over Time" | "Subscriptions">("Subscriptions");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([startOfYear(new Date()), endOfYear(new Date())]);
  const [activeFilter, setActiveFilter] = useState<string | null>('currentYear');
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    if (startDate && endDate) {
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      dispatch(
        fetchLineGraphData({
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
        })
      );
    }
  }, [startDate, endDate, dispatch, refresh]);

  const handleDateChange = (range: [Date | null, Date | null], filter: string | null) => {    
    setDateRange(range);
    setActiveFilter(filter);
  };

  const dataForChart = activeTab === 'Downloads Over Time' 
    ? lineGraphData?.downloads 
    : lineGraphData?.subscriptions;

  const getInterval = (dataLength: number | undefined) => {
    if (!dataLength) return 0;
    if (dataLength > 15) {
        return Math.floor(dataLength / 15);
    }
    return 0;
  }

  const CustomTooltip = ({
    active,
    payload,
    label
  }: {
    active?: boolean;
    payload?: any;
    label?: string;
  }) => {
    if (active && payload && payload.length) {      
      return (
        <div className={styles.customTooltip}>
          <div className={styles.tooltipLabel}>{label}</div>
          <div className={cn(styles.nextTooltipLabel, styles.tooltipValLabel)}><span className={styles.lagendDot} /> {payload[0].value}</div>
        </div>
      );
    }
    return null;
  };

  const CustomTick: React.FC<CustomTickProps> = ({ x, y, payload }) => {
    return (
      <text
        x={x - 10}
        y={y + 20}
        fill={"var(--lightGrayishNavy2)"}
        fontSize={12}
        fontWeight={"500" as const}
        style={{ fontFamily: "var(--basefont)" }}
      >
        {payload.value}
      </text>
    );
  };

  return (
    <Card bsPrefix={cn(styles.lineChardCard)}>
      <Card.Header bsPrefix={cn(styles.lineChardCardHeader)}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardHeaderTitles}>
            {tabTitles.map((title, index)=>( 
              <Card.Title 
                as={'div'} 
                bsPrefix={cn(styles.titleValue, title === activeTab && styles.activeTitle)}
                key={index}
                onClick={() => setActiveTab(title as any)}
              >
                  {title}
              </Card.Title>
            ))}
          </div>
          <div className={styles.downloadsWrap}>
            <div className={styles.downloadsCard}>
              <Card.Title as={'div'} className={styles.downloadsCardTitle}>
                {lineGraphLoading === 'succeeded' && lineGraphData ? (activeTab === 'Downloads Over Time' ? lineGraphData.totalDownloads : lineGraphData.totalSubs) : '...'}
              </Card.Title>
              <Card.Text as={'div'} className={styles.downloadsCardText}>
                {activeTab === 'Downloads Over Time' ? 'Total Downloads' : 'Total Subscriptions'}
              </Card.Text>
            </div>
          </div>
        </div>
        <div className={styles.cardHeaderRight}>
          <div className={styles.chartFilter}>
            <div className={styles.dateFilter}>
              <Calender calenderBtnTheme={CalenderBtnTheme.LIGHT} onDateChange={handleDateChange} dateRange={dateRange} activeFilter={activeFilter}/>
            </div>
          </div>
           <div className={styles.customLegend}>
            {/* <span className={cn(styles.customLegendPrev, styles.lagendLabel)}><span className={styles.lagendDot} /> Jun 21</span> */}
            {/* <span className={cn(styles.customLegendCurrent, styles.lagendLabel)}><span className={styles.lagendDot} /> Jun 22</span> */}
          </div>
        </div>
      </Card.Header>

      {/* Chart */}
      <Card.Body bsPrefix={styles.lineChardCardBody} style={{ height: 297 }}>
        {lineGraphLoading === 'pending' && <p>Loading...</p>}
        {lineGraphLoading === 'succeeded' && dataForChart && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dataForChart} margin={{ top: 0, right: 35, bottom: 35, left: 25 }}>
              <Line type="monotone" dataKey="value" stroke="var(--vividNavy)" strokeWidth={3} dot={{ r: 4 }} />
              <CartesianGrid 
                vertical={false} 
                horizontal={true} 
                stroke="var(--paleNavy)"
                strokeWidth={1} 
                strokeDasharray="2 4"
              />
              <XAxis dataKey="name" 
                interval={getInterval(dataForChart?.length)}
                axisLine={true}
                tickLine={true}
                tickMargin={10}
                tick={<CustomTick x={0} y={0} payload={{
                  value: ""
                }} />}
                label={{
                  value:
                    lineGraphData?.period === "daily"
                        ? "Time ->"
                        : lineGraphData?.period === "weekly"
                        ? "Dates ->"
                        : lineGraphData?.period === "monthly"
                        ? "Weeks ->"
                        : "Months ->",
                  position: "insideBottom",
                  offset: -30,
                  fill: "var(--black2)",
                  fontSize: 14,
                  fontWeight: 700,
                }}
              />
              <YAxis 
                axisLine={true}
                tickLine={true}
                tick={{ 
                  fill: "var(--lightGrayishNavy2)",
                  fontSize:12,
                  fontWeight: 500,
                  fontFamily: "var(--basefont)",
                }}
                label={{
                  value: activeTab === "Downloads Over Time" ? "Downloads ->" : "Subscriptions ->",
                  angle: -90,
                  position: "insideLeft",
                  fill: "var(--black2)",
                  fontSize: 14,
                  fontWeight: 700,
                }}
                tickMargin={5}
                padding={{ top: 0, bottom: 0 }}
              />
              <Tooltip content={<CustomTooltip />} />
            </LineChart>
          </ResponsiveContainer>
        )}
        {lineGraphLoading === 'failed' && <p>Error loading data.</p>}
      </Card.Body>
    </Card>
  );
}

export default React.memo(DownloadsOverChart);
